import React, { useEffect, useMemo, useState } from 'react';
import { fastapi } from '../../services/api';

type AvailableUser = {
  id: number;
  email: string;
  is_active: boolean;
  note?: string;
  comercio_key?: string;
  attempt_count: number;
  last_attempt_at?: string;
  created_at: string;
};

type AccessAttempt = {
  id: number;
  email: string;
  attempt_type: string;
  ip_address?: string;
  user_agent?: string;
  details?: Record<string, unknown>;
  notification_suppressed: boolean;
  notification_status?: string;
  created_at: string;
};

const templates = {
  es: {
    subject: 'Tu acceso a Taratic ha sido aprobado',
    message: 'Tu correo ya está autorizado para usar Taratic. Regístrate si todavía no tienes una cuenta, o inicia sesión si ya la tienes. Si necesitas ayuda, responde a este correo.',
  },
  en: {
    subject: 'Your access to Taratic has been approved',
    message: 'Your email is now authorized to use Taratic. Register if you do not have an account yet, or log in if you already have one. Reply to this email if you need help.',
  },
};

const AdminAvailableUsersPage: React.FC = () => {
  const [users, setUsers] = useState<AvailableUser[]>([]);
  const [attempts, setAttempts] = useState<AccessAttempt[]>([]);
  const [view, setView] = useState<'users' | 'requests'>('users');
  const [state, setState] = useState<'all' | 'active' | 'suspended'>('all');
  const [search, setSearch] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [emailModal, setEmailModal] = useState<AvailableUser | null>(null);
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const [subject, setSubject] = useState(templates.es.subject);
  const [message, setMessage] = useState(templates.es.message);
  const [historyUser, setHistoryUser] = useState<AvailableUser | null>(null);
  const [emailLogs, setEmailLogs] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);

  const load = async () => {
    setError('');
    try {
      const [usersResponse, attemptsResponse] = await Promise.all([
        fastapi.get('/admin/available-users', { params: { search, state } }),
        fastapi.get('/admin/access-attempts', { params: { limit: 250 } }),
      ]);
      setUsers(usersResponse.data);
      setAttempts(attemptsResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Could not load access-control data.');
    }
  };

  useEffect(() => { void load(); }, [state]);

  const requestEmails = useMemo(() => new Set(attempts.map((item) => item.email)), [attempts]);

  const addUser = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await fastapi.post('/admin/available-users', { email, note: note || null });
      setEmail('');
      setNote('');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Could not add email.');
    } finally {
      setBusy(false);
    }
  };

  const updateUser = async (user: AvailableUser, changes: Record<string, unknown>) => {
    setBusy(true);
    try {
      await fastapi.patch(`/admin/available-users/${user.id}`, changes);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Could not update access.');
    } finally {
      setBusy(false);
    }
  };

  const openEmail = (user: AvailableUser) => {
    setLanguage('es');
    setSubject(templates.es.subject);
    setMessage(templates.es.message);
    setEmailModal(user);
  };

  const changeLanguage = (next: 'es' | 'en') => {
    setLanguage(next);
    setSubject(templates[next].subject);
    setMessage(templates[next].message);
  };

  const queueEmail = async () => {
    if (!emailModal) return;
    setBusy(true);
    try {
      await fastapi.post(`/admin/available-users/${emailModal.id}/send-access-email`, { language, subject, message });
      setEmailModal(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Could not queue email.');
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async (user: AvailableUser) => {
    setHistoryUser(user);
    try {
      const [emailsResponse, auditResponse] = await Promise.all([
        fastapi.get(`/admin/available-users/${user.id}/emails`),
        fastapi.get(`/admin/available-users/${user.id}/audit`),
      ]);
      setEmailLogs(emailsResponse.data);
      setAuditLogs(auditResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Could not load history.');
    }
  };

  const retryEmail = async (outboxId: number) => {
    await fastapi.post(`/admin/email-outbox/${outboxId}/retry`);
    if (historyUser) await openHistory(historyUser);
  };

  const addFromRequest = (attemptEmail: string) => {
    setView('users');
    setEmail(attemptEmail);
    setNote('Added from access request');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="p-8 space-y-7">
      <header>
        <h1 className="text-4xl font-black">Available Users</h1>
        <p className="mt-2 text-white/55">Exact-email access control. Suspensions revoke existing sessions immediately.</p>
      </header>

      {error && <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-red-200">{String(error)}</div>}

      <div className="flex gap-2 rounded-2xl border border-white/10 bg-white/5 p-2 w-fit">
        <button onClick={() => setView('users')} className={`rounded-xl px-5 py-2 font-bold ${view === 'users' ? 'bg-indigo-600' : 'text-white/60'}`}>Allowlist</button>
        <button onClick={() => setView('requests')} className={`rounded-xl px-5 py-2 font-bold ${view === 'requests' ? 'bg-indigo-600' : 'text-white/60'}`}>Access requests ({requestEmails.size})</button>
      </div>

      {view === 'users' ? <>
        <form onSubmit={addUser} className="grid gap-3 rounded-2xl border border-white/10 bg-white/5 p-5 md:grid-cols-[1fr_1fr_auto]">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none focus:border-indigo-400" />
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (optional)" className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 outline-none focus:border-indigo-400" />
          <button disabled={busy} className="rounded-xl bg-indigo-600 px-6 py-3 font-bold hover:bg-indigo-500 disabled:opacity-50">Add email</button>
        </form>

        <div className="flex flex-wrap gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void load(); }} placeholder="Search email or note" className="min-w-72 rounded-xl border border-white/10 bg-white/5 px-4 py-2" />
          <button onClick={() => void load()} className="rounded-xl bg-white/10 px-4 py-2">Search</button>
          {(['all', 'active', 'suspended'] as const).map((item) => <button key={item} onClick={() => setState(item)} className={`rounded-xl px-4 py-2 capitalize ${state === item ? 'bg-pink-600' : 'bg-white/5 text-white/60'}`}>{item}</button>)}
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
          <table className="w-full text-left text-sm">
            <thead className="bg-black/20 text-white/50"><tr><th className="p-4">Email</th><th className="p-4">Account</th><th className="p-4">Attempts</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr></thead>
            <tbody>
              {users.map((user) => <tr key={user.id} className="border-t border-white/10">
                <td className="p-4"><div className="font-semibold">{user.email}</div><div className="mt-1 text-xs text-white/40">{user.note || 'No note'}</div></td>
                <td className="p-4 text-white/65">{user.comercio_key || 'Not registered'}</td>
                <td className="p-4 text-white/65">{user.attempt_count || 0}</td>
                <td className="p-4"><span className={`rounded-full px-3 py-1 text-xs font-bold ${user.is_active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>{user.is_active ? 'Allowed' : 'Suspended'}</span></td>
                <td className="p-4"><div className="flex justify-end gap-2">
                  {user.is_active && <button onClick={() => openEmail(user)} className="rounded-lg bg-blue-500/15 px-3 py-2 text-blue-200">Send email</button>}
                  <button onClick={() => void openHistory(user)} className="rounded-lg bg-white/10 px-3 py-2 text-white/70">History</button>
                  <button onClick={() => { const next = window.prompt('Internal note', user.note || ''); if (next !== null) void updateUser(user, { note: next }); }} className="rounded-lg bg-white/10 px-3 py-2 text-white/70">Edit note</button>
                  <button disabled={busy} onClick={() => void updateUser(user, { is_active: !user.is_active })} className={`rounded-lg px-3 py-2 ${user.is_active ? 'bg-red-500/15 text-red-200' : 'bg-emerald-500/15 text-emerald-200'}`}>{user.is_active ? 'Suspend' : 'Restore'}</button>
                </div></td>
              </tr>)}
            </tbody>
          </table>
        </div>
      </> : <div className="space-y-3">
        {attempts.map((attempt) => <div key={attempt.id} className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><div className="font-bold">{attempt.email}</div><div className="mt-1 text-sm text-white/50">{attempt.attempt_type} · {new Date(attempt.created_at).toLocaleString()} · {attempt.ip_address || 'IP unavailable'}</div></div>
            <button onClick={() => addFromRequest(attempt.email)} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold">Add to allowlist</button>
          </div>
          <div className="mt-3 text-xs text-white/45">Notification: {attempt.notification_suppressed ? 'deduplicated' : (attempt.notification_status || 'queued')}</div>
        </div>)}
      </div>}

      {emailModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4">
        <div className="w-full max-w-2xl rounded-3xl border border-white/10 bg-gray-900 p-7 shadow-2xl">
          <h2 className="text-2xl font-black">Send access email</h2>
          <p className="mt-1 text-white/50">Recipient: {emailModal.email}</p>
          <div className="mt-5 flex gap-2">{(['es', 'en'] as const).map((item) => <button key={item} onClick={() => changeLanguage(item)} className={`rounded-lg px-4 py-2 ${language === item ? 'bg-indigo-600' : 'bg-white/10'}`}>{item === 'es' ? 'Español' : 'English'}</button>)}</div>
          <label className="mt-5 block text-sm text-white/60">Subject</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-2 w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3" />
          <label className="mt-5 block text-sm text-white/60">Message</label>
          <textarea rows={7} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-2 w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3" />
          <div className="mt-6 flex justify-end gap-3"><button onClick={() => setEmailModal(null)} className="btn-taratic btn-ghost-dark rounded-xl px-5 py-3">Cancel</button><button disabled={busy || !subject.trim() || !message.trim()} onClick={() => void queueEmail()} className="rounded-xl bg-indigo-600 px-5 py-3 font-bold disabled:opacity-50">Queue email</button></div>
        </div>
      </div>}

      {historyUser && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4">
        <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-white/10 bg-gray-900 p-7 shadow-2xl">
          <div className="flex items-start justify-between"><div><h2 className="text-2xl font-black">Audit & email history</h2><p className="text-white/50">{historyUser.email}</p></div><button onClick={() => setHistoryUser(null)} className="rounded-lg bg-white/10 px-3 py-2">Close</button></div>
          <h3 className="mt-7 font-bold">Manual emails</h3>
          <div className="mt-3 space-y-2">{emailLogs.length === 0 && <p className="text-sm text-white/40">No manual emails sent.</p>}{emailLogs.map((entry) => <div key={entry.id} className="rounded-xl bg-white/5 p-4 text-sm"><div className="flex justify-between gap-3"><span className="font-semibold">{entry.subject}</span><span className={entry.status === 'failed' ? 'text-red-300' : 'text-emerald-300'}>{entry.status}</span></div><div className="mt-1 text-white/40">Requested by {entry.requested_by} · {new Date(entry.created_at).toLocaleString()}</div>{entry.last_error && <div className="mt-2 text-red-300">{entry.last_error}</div>}{entry.status === 'failed' && <button onClick={() => void retryEmail(entry.id)} className="mt-3 rounded-lg bg-indigo-600 px-3 py-2 font-bold">Retry</button>}</div>)}</div>
          <h3 className="mt-7 font-bold">Allowlist audit</h3>
          <div className="mt-3 space-y-2">{auditLogs.map((entry) => <div key={entry.id} className="rounded-xl bg-white/5 p-4 text-sm"><span className="font-semibold capitalize">{String(entry.action).replaceAll('_', ' ')}</span><div className="mt-1 text-white/40">{entry.actor_email} · {new Date(entry.created_at).toLocaleString()}</div></div>)}</div>
        </div>
      </div>}
    </div>
  );
};

export default AdminAvailableUsersPage;
